import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Megaphone, Plus, Trash2, ArrowUp, ArrowDown, Save, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const PARTNER_DESCRIPTION_MAX = 280;

const emptyRow = () => ({
  key: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  partner_name: '',
  description: '',
  image_url: '',
  target_url: '',
  enabled: true
});

const AdminDigestAds = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tableMissing, setTableMissing] = useState(false);
  const [tableMessage, setTableMessage] = useState('');
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/admin/digest-ads', { withCredentials: true });
      if (res.data.success) {
        setTableMissing(!!res.data.tableMissing);
        setTableMessage(res.data.message || '');
        const list = res.data.banners || [];
        setRows(
          list.map((b) => ({
            key: String(b.id),
            id: b.id,
            partner_name: b.partner_name || '',
            description: (b.description != null ? String(b.description) : '').slice(0, PARTNER_DESCRIPTION_MAX),
            image_url: b.image_url || '',
            target_url: b.target_url || '',
            enabled: b.enabled !== false
          }))
        );
      } else {
        toast.error(res.data.error || 'Failed to load');
      }
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Access denied. Admin only.');
        navigate('/trend-spotter');
      } else {
        toast.error('Failed to load digest ads');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (user && user.email && user.email.toLowerCase() !== 'info@showstop.ai') {
      toast.error('Access denied. Admin only.');
      navigate('/trend-spotter');
      return;
    }
    load();
  }, [user, navigate, load]);

  const updateRow = (key, field, value) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };

  const move = (index, dir) => {
    setRows((prev) => {
      const j = index + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  };

  const removeRow = (key) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
  };

  const save = async () => {
    const payload = rows.map((r) => ({
      partner_name: r.partner_name,
      description: (r.description != null ? String(r.description) : '').slice(0, PARTNER_DESCRIPTION_MAX),
      image_url: r.image_url,
      target_url: r.target_url,
      enabled: r.enabled
    }));
    try {
      setSaving(true);
      const res = await axios.put('/api/admin/digest-ads', { banners: payload }, { withCredentials: true });
      if (res.data.success) {
        toast.success('Digest ads saved');
        const list = res.data.banners || [];
        setRows(
          list.map((b) => ({
            key: String(b.id),
            id: b.id,
            partner_name: b.partner_name || '',
            description: (b.description != null ? String(b.description) : '').slice(0, PARTNER_DESCRIPTION_MAX),
            image_url: b.image_url || '',
            target_url: b.target_url || '',
            enabled: b.enabled !== false
          }))
        );
        setTableMissing(false);
        setTableMessage('');
      } else {
        toast.error(res.data.error || 'Save failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-zinc-950">
        <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="p-2 rounded-lg bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-zinc-300" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Megaphone className="h-7 w-7 text-indigo-600" />
              Weekly digest — partner ads
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setRows((prev) => [...prev, emptyRow()])}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-zinc-800 border border-gray-300 dark:border-zinc-600 text-gray-800 dark:text-zinc-100 hover:bg-gray-50 dark:hover:bg-zinc-700"
            >
              <Plus className="h-4 w-4" />
              Add banner
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </button>
          </div>
        </div>

        {tableMissing && (
          <div className="mb-6 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100 text-sm">
            <strong>Database setup required.</strong>{' '}
            {tableMessage || 'Run scripts/add-weekly-digest-ad-banners.sql on your Postgres database, then refresh.'}
          </div>
        )}

        <p className="text-gray-600 dark:text-zinc-400 text-sm mb-6">
          <strong>Order in the email:</strong> Banner 1 goes after the summary stats (before the trend list). Banner 2
          appears after the <em>first</em> trend, banner 3 after the second, and so on. Any extra banners after the last
          trend are stacked at the bottom of the digest. If there are no trends this week, all enabled banners show after
          the stats. Labels use &quot;Partner&quot; for transparency. Optional partner description (up to {PARTNER_DESCRIPTION_MAX}{' '}
          characters) appears under the banner image in the email. URLs must use{' '}
          <code className="text-xs bg-gray-200 dark:bg-zinc-800 px-1 rounded">https://</code>. Disable a slot to keep it
          on file without sending it.
        </p>

        {rows.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-zinc-500 border border-dashed border-gray-300 dark:border-zinc-700 rounded-xl">
            No banners yet. Add one to feature a partner in the digest.
          </div>
        ) : (
          <div className="space-y-6">
            {rows.map((row, index) => (
              <div
                key={row.key}
                className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-700 p-4 shadow-sm"
              >
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <span className="text-sm font-semibold text-gray-700 dark:text-zinc-300">Banner {index + 1}</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-zinc-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={row.enabled}
                        onChange={(e) => updateRow(row.key, 'enabled', e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      Enabled
                    </label>
                    <button
                      type="button"
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                      className="p-1.5 rounded border border-gray-200 dark:border-zinc-600 disabled:opacity-40"
                      aria-label="Move up"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(index, 1)}
                      disabled={index === rows.length - 1}
                      className="p-1.5 rounded border border-gray-200 dark:border-zinc-600 disabled:opacity-40"
                      aria-label="Move down"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeRow(row.key)}
                      className="p-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950/50"
                      aria-label="Remove"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-1">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-zinc-500 mb-1">Partner name (alt text)</label>
                    <input
                      type="text"
                      value={row.partner_name}
                      onChange={(e) => updateRow(row.key, 'partner_name', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-gray-900 dark:text-zinc-100 text-sm"
                      placeholder="e.g. Acme Studio"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-zinc-500 mb-1">Banner image URL (https)</label>
                    <input
                      type="url"
                      value={row.image_url}
                      onChange={(e) => updateRow(row.key, 'image_url', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-gray-900 dark:text-zinc-100 text-sm font-mono"
                      placeholder="https://cdn.example.com/banner.png"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-zinc-500 mb-1">Click-through URL (https)</label>
                    <input
                      type="url"
                      value={row.target_url}
                      onChange={(e) => updateRow(row.key, 'target_url', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-gray-900 dark:text-zinc-100 text-sm font-mono"
                      placeholder="https://partner.com/offer"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-zinc-500 mb-1">
                      Partner description <span className="font-normal text-gray-400">(optional, max {PARTNER_DESCRIPTION_MAX} chars)</span>
                    </label>
                    <textarea
                      value={row.description || ''}
                      onChange={(e) =>
                        updateRow(row.key, 'description', e.target.value.slice(0, PARTNER_DESCRIPTION_MAX))
                      }
                      maxLength={PARTNER_DESCRIPTION_MAX}
                      rows={3}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-gray-900 dark:text-zinc-100 text-sm resize-y min-h-[4.5rem]"
                      placeholder="Short line about the partner or offer — shows under the banner in the email."
                    />
                    <p className="text-xs text-gray-400 mt-1 text-right">
                      {(row.description || '').length}/{PARTNER_DESCRIPTION_MAX}
                    </p>
                  </div>
                </div>
                {row.image_url && /^https:\/\//i.test(row.image_url) && (
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-zinc-800">
                    <p className="text-xs text-gray-500 mb-2">Preview</p>
                    <div className="max-w-md rounded-lg overflow-hidden border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800">
                      <img
                        src={row.image_url}
                        alt={row.partner_name || 'Banner preview'}
                        className="w-full h-auto max-h-48 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      {(row.description || '').trim() ? (
                        <div className="px-3 py-2 border-t border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900/50">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-500 mb-1">Partner</p>
                          <p className="text-sm text-gray-600 dark:text-zinc-400 leading-snug whitespace-pre-wrap">
                            {(row.description || '').trim()}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDigestAds;
